import { useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Button from '@mui/material/Button';
import { editServiceStatus, postServiceStatus } from '../../../Services/Search.services';
import { datetimeFormat } from '../../../Constants/Date.constant';
import { removeTimezoneOffset, toIsoIgnoreTimezone } from '../../../Utils/Utils';
import { AvlTextField } from '../../Shared/Inputs/FieldText/AvlTextField';
import { AvlCheckbox } from '../../Shared/Inputs/Checkbox/AvlCheckbox';
import { AvlDateTimePickerField } from '../../Shared/Inputs/DateTimePicker/AvlDateTimePickerField';
import SkeletonComponent from '../../SkeletonComponent';
import styles from './ServiceStatusForm.module.css';

const FieldKeys = {
    name: 'name',
    message: 'message',
    isDown: 'isDown',
    startAt: 'startAt',
    endAt: 'endAt',
};

const FieldOptions = {
    [FieldKeys.name]: {
        label: 'Service Name',
        isVisible: true,
        isRequired: true,
        type: 'text',
    },
    [FieldKeys.message]: {
        label: 'Status Message',
        isVisible: true,
        isRequired: false,
        type: 'text',
        isMultiline: true,
        rows: 6,
    },
    [FieldKeys.isDown]: {
        label: 'Is Service Down',
        isVisible: true,
        isRequired: false,
        type: 'checkbox',
    },
    [FieldKeys.startAt]: {
        label: 'Start Date & Time',
        isVisible: true,
        isRequired: true,
        type: 'datetime',
    },
    [FieldKeys.endAt]: {
        label: 'End Date & Time',
        isVisible: true,
        isRequired: true,
        type: 'datetime',
    },
};

export default function ServiceStatusForm(
    {
        initialData = null,
        isEditMode = false,
        onSubmitSuccess,
    }) {
    const [isLoading, setIsLoading] = useState(false);

    const validationSchema = yup.object({
        [FieldKeys.name]: yup.string()
            .required('Service name is required'),
        [FieldKeys.message]: yup.string(),
        [FieldKeys.isDown]: yup.boolean(),
        [FieldKeys.startAt]: yup.date()
            .required('Start date and time is required'),
        [FieldKeys.endAt]: yup.date()
            .required('End date and time is required')
            .min(yup.ref(FieldKeys.startAt), 'End date must be after start date'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            [FieldKeys.name]: initialData?.name || '',
            [FieldKeys.message]: initialData?.message || '',
            [FieldKeys.isDown]: initialData?.is_down ?? false,
            [FieldKeys.startAt]: initialData?.start_at
                ? (isEditMode ? removeTimezoneOffset(new Date(initialData.start_at)) : new Date(initialData.start_at))
                : new Date(),
            [FieldKeys.endAt]: initialData?.end_at
                ? (isEditMode ? removeTimezoneOffset(new Date(initialData.end_at)) : new Date(initialData.end_at))
                : new Date(),
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            handleSubmit(values);
        },
    });

    const handleSubmit = async (values) => {
        setIsLoading(true);
        const isSecondsDisabled = true;

        try {
            const requestData = {
                message: values.message,
                is_down: values.isDown,
                end_at: toIsoIgnoreTimezone(values.endAt, isSecondsDisabled),
                start_at: toIsoIgnoreTimezone(values.startAt, isSecondsDisabled),
            };

            if (isEditMode) {
                if (!initialData?.id) {
                    throw new Error('Service ID is required for editing');
                }
                await editServiceStatus(initialData.id, requestData);
            } else {
                requestData.name = values.name;
                await postServiceStatus(requestData);
            }

            formik.resetForm();
            onSubmitSuccess?.();
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} service:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    const renderTextField = (fieldKey) => {
        const fieldConfig = FieldOptions[fieldKey];
        const hasError = formik.touched[fieldKey] ? !!formik.errors[fieldKey] : false;
        const errorMessage = formik.touched[fieldKey] && formik.errors[fieldKey];

        return (
            <AvlTextField
                className={styles.field}
                required={fieldConfig.isRequired}
                label={fieldConfig.label}
                name={fieldKey}
                type={fieldConfig.type}
                value={formik.values[fieldKey] ?? ''}
                onChange={formik.handleChange}
                error={hasError}
                helperText={errorMessage}
                multiline={fieldConfig.isMultiline}
                rows={fieldConfig.rows}
                disabled={isLoading || (isEditMode && fieldKey === FieldKeys.name)}
            />
        );
    };

    const renderDateTimeField = (fieldKey) => {
        const fieldConfig = FieldOptions[fieldKey];
        const hasError = formik.touched[fieldKey] ? !!formik.errors[fieldKey] : false;
        const errorMessage = formik.touched[fieldKey] && formik.errors[fieldKey];

        return (
            <AvlDateTimePickerField
                className={styles.field}
                required={fieldConfig.isRequired}
                label={fieldConfig.label}
                name={fieldKey}
                format={datetimeFormat}
                value={formik.values[fieldKey]}
                onChange={(newValue) => formik.setFieldValue(fieldKey, newValue)}
                error={hasError}
                helperText={errorMessage}
                disabled={isLoading}
            />
        );
    };

    const renderCheckboxField = (fieldKey) => {
        const fieldConfig = FieldOptions[fieldKey];

        return (
            <AvlCheckbox
                label={fieldConfig.label}
                name={fieldKey}
                value={formik.values[fieldKey]}
                onChange={formik.handleChange}
                disabled={isLoading}
            />
        );
    };

    if (isLoading && !initialData && isEditMode) {
        return <SkeletonComponent />;
    }

    return (
        <div className={styles.form}>
            <div className={styles.sections}>
                <div className={`${styles.section} ${styles.initialData}`}>
                    {!isEditMode && renderTextField(FieldKeys.name)}
                    {renderTextField(FieldKeys.message)}
                    {renderDateTimeField(FieldKeys.startAt)}
                    {renderDateTimeField(FieldKeys.endAt)}
                    {renderCheckboxField(FieldKeys.isDown)}
                </div>
            </div>
            <div className={styles.controlsContainer}>
                <Button
                    className="button"
                    variant="outlined"
                    size="small"
                    color="primary"
                    disabled={isLoading || !formik.dirty}
                    onClick={() => formik.resetForm()}
                >
                    Reset
                </Button>
                <Button
                    className="button"
                    variant="contained"
                    size="small"
                    color="primary"
                    disabled={isLoading || (isEditMode && !formik.dirty)}
                    onClick={() => {
                        formik.submitForm()
                            .catch(console.error);
                    }}
                >
                    {isEditMode ? 'Update' : 'Create'}
                </Button>
            </div>
        </div>
    );
}
